/**
 * ReimbursementsTab.jsx — HR/Admin view
 * Import into ExpenseTracker.jsx:
 *   import ReimbursementsTab from "./ReimbursementsTab";
 * Add to TABS array:
 *   {key:"reimbursements", label:"Reimbursements", icon:Receipt}
 * Render:
 *   {tab==="reimbursements" && <ReimbursementsTab flash={flash}/>}
 */

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  CheckCircle, XCircle, Clock, DollarSign, Download,
  Eye, X, FileText, Users, TrendingUp, AlertCircle,
  ChevronDown, Search, Filter, Send, Receipt,
} from "lucide-react";
import { apiBaseUrl } from "services/config";
import { useTheme } from "context/ThemeContext";

const API = apiBaseUrl;

const STATUS_CFG = {
  draft:    { label:"Draft",    color:"dim"    },
  pending:  { label:"Pending",  color:"amber"  },
  approved: { label:"Approved", color:"green"  },
  rejected: { label:"Rejected", color:"red"    },
  paid:     { label:"Paid",     color:"cyan"   },
};

const CATEGORY_LABELS = {
  travel:"Travel & Transport", accommodation:"Accommodation",
  meals:"Meals & Entertainment", office_supplies:"Office Supplies",
  software:"Software & Tools", training:"Training & Courses",
  medical:"Medical", courier:"Courier & Delivery",
  telephone:"Telephone & Internet", fuel:"Fuel", other:"Other",
};

const fmtINR = n => `₹${Number(n||0).toLocaleString("en-IN",{maximumFractionDigits:0})}`;

// ── Shared UI ──────────────────────────────────────────────────────
function Card({children,style={}}) {
  const {T} = useTheme();
  return <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,...style}}>{children}</div>;
}
function StatCard({label,value,sub,colorKey="cyan",icon}) {
  const {T} = useTheme();
  const color = T[colorKey]||T.cyan;
  return (
    <Card style={{padding:"18px 20px",flex:1,minWidth:140}}>
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
        <div style={{fontSize:10,color:T.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em"}}>{label}</div>
        {icon && React.createElement(icon,{size:14,style:{color,opacity:0.7}})}
      </div>
      <div style={{fontSize:22,fontWeight:900,color,fontFamily:"JetBrains Mono,monospace"}}>{value}</div>
      {sub && <div style={{fontSize:11,color:T.dim,marginTop:3}}>{sub}</div>}
    </Card>
  );
}
function Btn({onClick,disabled,variant="primary",size="md",children,style={}}) {
  const {T} = useTheme();
  const bg  = variant==="primary"?`linear-gradient(135deg,${T.cyan},${T.purple})`:T.toggle_bg;
  const col = variant==="primary"?"#fff":variant==="danger"?T.red:variant==="success"?T.green:T.muted;
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding:size==="sm"?"5px 12px":"9px 20px",borderRadius:8,
      border:variant==="primary"?"none":`1px solid ${col}30`,
      background:variant==="primary"?bg:variant==="danger"?`${T.red}12`:variant==="success"?`${T.green}12`:T.toggle_bg,
      color:col,cursor:disabled?"not-allowed":"pointer",opacity:disabled?0.5:1,
      fontFamily:"Outfit,sans-serif",fontSize:size==="sm"?11:13,fontWeight:700,
      display:"inline-flex",alignItems:"center",gap:6,...style,
    }}>{children}</button>
  );
}

function StatusBadge({status}) {
  const {T} = useTheme();
  const cfg = STATUS_CFG[status] || STATUS_CFG.pending;
  const color = T[cfg.color] || T.muted;
  return (
    <span style={{fontSize:11,padding:"3px 10px",borderRadius:20,fontWeight:700,
      background:`${color}18`,color,border:`1px solid ${color}30`}}>
      {cfg.label}
    </span>
  );
}

// ── Claim detail modal ─────────────────────────────────────────────
function ClaimModal({claim,onClose,onAction,flash}) {
  const {T} = useTheme();
  const [action,    setAction]    = useState(null); // "approve"|"reject"
  const [notes,     setNotes]     = useState("");
  const [approvedAmt, setApprovedAmt] = useState(claim.total_amount);
  const [loading,   setLoading]   = useState(false);

  const inp = {
    background:T.inp_bg,border:`1px solid ${T.border}`,borderRadius:8,
    color:T.text,fontSize:13,padding:"9px 12px",outline:"none",
    fontFamily:"Outfit,sans-serif",boxSizing:"border-box",width:"100%",
  };

  const submit = async () => {
    setLoading(true);
    try {
      if(action==="approve") {
        await axios.put(`${API}/reimbursements/${claim.id}/approve`,
          {notes, approved_amount: parseFloat(approvedAmt)});
        flash("✅ Claim approved");
      } else if(action==="reject") {
        if(!notes.trim()){flash("❌ Please enter rejection reason");setLoading(false);return;}
        await axios.put(`${API}/reimbursements/${claim.id}/reject`, {notes});
        flash("✅ Claim rejected");
      }
      // Note: "paid" action is handled directly by the Mark Paid button
      onAction(); onClose();
    } catch(e) { flash("❌ "+(e.response?.data?.detail||"Error")); }
    finally { setLoading(false); }
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:9998,
      display:"flex",alignItems:"center",justifyContent:"center",padding:20,fontFamily:"Outfit,sans-serif"}}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,
        width:620,maxHeight:"88vh",overflow:"hidden",display:"flex",flexDirection:"column"}}>

        {/* Header */}
        <div style={{padding:"18px 24px",borderBottom:`1px solid ${T.border}`,
          background:T.card2,display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
          <div>
            <h3 style={{margin:0,fontSize:16,fontWeight:700,color:T.text}}>{claim.title}</h3>
            <div style={{fontSize:12,color:T.muted,marginTop:3}}>
              {claim.employee_name} · {claim.department} · Submitted {claim.submitted_at}
            </div>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <StatusBadge status={claim.status}/>
            <button onClick={onClose} style={{background:"none",border:"none",
              cursor:"pointer",color:T.muted}}><X size={16}/></button>
          </div>
        </div>

        <div style={{overflowY:"auto",flex:1,padding:"20px 24px",
          display:"flex",flexDirection:"column",gap:16}}>

          {/* Amount summary */}
          <div style={{display:"flex",gap:12}}>
            <div style={{flex:1,padding:"14px 16px",borderRadius:10,
              background:`${T.cyan}08`,border:`1px solid ${T.cyan}20`}}>
              <div style={{fontSize:10,color:T.muted,fontWeight:700,
                textTransform:"uppercase",marginBottom:4}}>Claimed Amount</div>
              <div style={{fontSize:22,fontWeight:900,color:T.cyan,
                fontFamily:"JetBrains Mono,monospace"}}>{fmtINR(claim.total_amount)}</div>
            </div>
            {claim.approved_amount && (
              <div style={{flex:1,padding:"14px 16px",borderRadius:10,
                background:`${T.green}08`,border:`1px solid ${T.green}20`}}>
                <div style={{fontSize:10,color:T.muted,fontWeight:700,
                  textTransform:"uppercase",marginBottom:4}}>Approved Amount</div>
                <div style={{fontSize:22,fontWeight:900,color:T.green,
                  fontFamily:"JetBrains Mono,monospace"}}>{fmtINR(claim.approved_amount)}</div>
              </div>
            )}
          </div>

          {/* Description */}
          {claim.description && (
            <div style={{fontSize:13,color:T.muted}}>{claim.description}</div>
          )}

          {/* Items */}
          <div>
            <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>
              Items ({claim.items?.length||0})
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {(claim.items||[]).map((item,i)=>(
                <div key={item.id} style={{padding:"12px 14px",borderRadius:8,
                  background:T.card2,border:`1px solid ${T.border}`}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:11,padding:"2px 8px",borderRadius:20,fontWeight:700,
                        background:`${T.purple}18`,color:T.purple}}>
                        {CATEGORY_LABELS[item.category]||item.category}
                      </span>
                      <span style={{fontSize:13,color:T.text,fontWeight:600}}>{item.description}</span>
                    </div>
                    <span style={{fontSize:14,fontWeight:800,color:T.cyan,
                      fontFamily:"JetBrains Mono,monospace"}}>{fmtINR(item.amount)}</span>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <span style={{fontSize:11,color:T.dim}}>{item.bill_date}</span>
                    {item.has_receipt ? (
                      <a href={item.receipt_path} target="_blank" rel="noreferrer"
                        style={{fontSize:11,color:T.cyan,textDecoration:"none",
                          display:"flex",alignItems:"center",gap:4}}>
                        <FileText size={11}/> View Receipt
                      </a>
                    ) : (
                      <span style={{fontSize:11,color:T.dim}}>No receipt</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Reviewer notes (if exists) */}
          {claim.reviewer_notes && (
            <div style={{padding:"12px 14px",borderRadius:8,
              background:`${claim.status==="rejected"?T.red:T.green}08`,
              border:`1px solid ${claim.status==="rejected"?T.red:T.green}25`}}>
              <div style={{fontSize:11,fontWeight:700,color:T.muted,marginBottom:4}}>
                REVIEWER NOTE
              </div>
              <div style={{fontSize:13,color:T.text}}>{claim.reviewer_notes}</div>
            </div>
          )}

          {/* Action panel */}
          {claim.status==="pending" && (
            <div style={{padding:"16px",borderRadius:10,
              background:T.card2,border:`1px solid ${T.border}`}}>
              <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:12}}>
                Review Action
              </div>
              {!action ? (
                <div style={{display:"flex",gap:10}}>
                  <Btn onClick={()=>setAction("approve")} variant="success">
                    <CheckCircle size={13}/>Approve
                  </Btn>
                  <Btn onClick={()=>setAction("reject")} variant="danger">
                    <XCircle size={13}/>Reject
                  </Btn>
                </div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {action==="approve" && (
                    <div>
                      <label style={{fontSize:11,color:T.muted,display:"block",
                        marginBottom:4,fontWeight:700}}>APPROVED AMOUNT</label>
                      <input type="number" value={approvedAmt}
                        onChange={e=>setApprovedAmt(e.target.value)}
                        style={{...inp,width:200}}/>
                      <div style={{fontSize:11,color:T.dim,marginTop:4}}>
                        Leave as claimed amount ({fmtINR(claim.total_amount)}) or adjust
                      </div>
                    </div>
                  )}
                  <div>
                    <label style={{fontSize:11,color:T.muted,display:"block",
                      marginBottom:4,fontWeight:700}}>
                      {action==="approve"?"NOTES (OPTIONAL)":"REJECTION REASON *"}
                    </label>
                    <textarea value={notes} onChange={e=>setNotes(e.target.value)}
                      placeholder={action==="approve"?"Add note for employee…":"Explain why this is rejected…"}
                      rows={3} style={{...inp,resize:"vertical"}}/>
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <Btn onClick={submit} disabled={loading}
                      variant={action==="approve"?"success":"danger"}>
                      {loading?"Processing…":action==="approve"
                        ?<><CheckCircle size={13}/>Confirm Approve</>
                        :<><XCircle size={13}/>Confirm Reject</>}
                    </Btn>
                    <Btn onClick={()=>setAction(null)} variant="ghost" size="sm">Cancel</Btn>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Mark paid — calls API directly to avoid React state closure bug */}
          {claim.status==="approved" && !claim.paid_at && (
            <div style={{padding:"14px 16px",borderRadius:10,
              background:`${T.green}06`,border:`1px solid ${T.green}20`,
              display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:13,fontWeight:600,color:T.text}}>Ready to pay</div>
                <div style={{fontSize:12,color:T.muted}}>
                  Approved: {fmtINR(claim.approved_amount||claim.total_amount)}
                </div>
              </div>
              <Btn variant="success" disabled={loading}
                onClick={async()=>{
                  setLoading(true);
                  try{
                    await axios.put(`${API}/reimbursements/${claim.id}/mark-paid`);
                    flash("✅ Marked as paid — added to expenses");
                    onAction(); onClose();
                  }catch(e){flash("❌ "+(e.response?.data?.detail||"Error"));}
                  finally{setLoading(false);}
                }}>
                <DollarSign size={13}/>Mark Paid
              </Btn>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// MAIN ADMIN TAB
// ══════════════════════════════════════════════════════════════════
export default function ReimbursementsTab({flash}) {
  const {T,theme} = useTheme();
  const [claims,   setClaims]   = useState([]);
  const [stats,    setStats]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [status,   setStatus]   = useState("pending");
  const [search,   setSearch]   = useState("");
  const [selected, setSelected] = useState(null);
  const [exporting,setExporting]= useState(false);

  const inp = {
    background:T.inp_bg,border:`1px solid ${T.border}`,borderRadius:8,
    color:T.text,fontSize:13,padding:"9px 12px",outline:"none",
    fontFamily:"Outfit,sans-serif",boxSizing:"border-box",
  };

  const load = useCallback(async() => {
    setLoading(true);
    try {
      const [cRes, sRes] = await Promise.all([
        axios.get(`${API}/reimbursements/all${status?`?status=${status}`:""}`),
        axios.get(`${API}/reimbursements/stats`),
      ]);
      setClaims(cRes.data||[]);
      setStats(sRes.data);
    } catch(e) { flash("❌ Failed to load"); }
    finally { setLoading(false); }
  },[status]);

  useEffect(()=>{load();},[load]);

  const loadDetail = async(claim) => {
    try {
      const r = await axios.get(`${API}/reimbursements/${claim.id}`);
      setSelected(r.data);
    } catch { flash("❌ Failed to load claim"); }
  };

  const exportPayroll = async() => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/reimbursements/export-payroll`);
      flash(`✅ ${r.data.message}`);
      load();
    } catch(e) { flash("❌ "+(e.response?.data?.detail||"Export failed")); }
    finally { setExporting(false); }
  };

  const exportCSV = async() => {
    try {
      const r = await axios.get(`${API}/reimbursements/export-csv`,{responseType:"blob"});
      const url=URL.createObjectURL(new Blob([r.data]));
      const a=document.createElement("a");a.href=url;a.download="Reimbursements.csv";a.click();
      URL.revokeObjectURL(url);
    } catch { flash("❌ Export failed"); }
  };

  const filtered = claims.filter(c=>
    !search || `${c.employee_name} ${c.title} ${c.employee_code}`.toLowerCase()
      .includes(search.toLowerCase())
  );

  const STATUS_TABS = ["pending","approved","rejected","paid","all"];

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>

      {/* Stats */}
      {stats && (
        <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
          <StatCard label="Pending Review" value={stats.pending_count}
            colorKey="amber" icon={Clock} sub={fmtINR(stats.pending_amount)}/>
          <StatCard label="Approved (Unpaid)" value={stats.approved_count}
            colorKey="green" icon={CheckCircle} sub={fmtINR(stats.approved_unpaid_amount)}/>
          <StatCard label="Total Paid" value={fmtINR(stats.total_paid_amount)}
            colorKey="cyan" icon={DollarSign}/>
          <div style={{display:"flex",gap:8,alignItems:"flex-end",paddingBottom:4}}>
            <Btn onClick={exportPayroll} disabled={exporting} variant="success" size="sm">
              <Send size={12}/>{exporting?"Exporting…":"Export to Payroll"}
            </Btn>
            <Btn onClick={exportCSV} variant="ghost" size="sm">
              <Download size={12}/>CSV
            </Btn>
          </div>
        </div>
      )}

      {/* Alert: approved unpaid */}
      {stats?.approved_unpaid_amount > 0 && (
        <div style={{padding:"11px 16px",borderRadius:10,
          background:`${T.amber}08`,border:`1px solid ${T.amber}25`,
          display:"flex",alignItems:"center",gap:8,fontSize:13}}>
          <AlertCircle size={14} style={{color:T.amber}}/>
          <span style={{color:T.muted}}>
            <strong style={{color:T.amber}}>{fmtINR(stats.approved_unpaid_amount)}</strong> in approved
            claims pending payment. Click <em>Export to Payroll</em> to process.
          </span>
        </div>
      )}

      {/* Controls */}
      <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
        {/* Status filter tabs */}
        <div style={{display:"flex",gap:4}}>
          {STATUS_TABS.map(s=>(
            <button key={s} onClick={()=>setStatus(s==="all"?"":s)} style={{
              padding:"6px 14px",borderRadius:8,fontSize:12,fontWeight:700,
              cursor:"pointer",fontFamily:"Outfit,sans-serif",border:"none",
              background:(status===s||(s==="all"&&status===""))
                ?`linear-gradient(135deg,${T.cyan},${T.purple})`:T.toggle_bg,
              color:(status===s||(s==="all"&&status===""))?"#fff":T.muted,
            }}>
              {s.charAt(0).toUpperCase()+s.slice(1)}
            </button>
          ))}
        </div>
        <div style={{position:"relative",flex:1,minWidth:200,maxWidth:300}}>
          <Search size={13} style={{position:"absolute",left:10,top:"50%",
            transform:"translateY(-50%)",color:T.dim}}/>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search employee / title…"
            style={{...inp,paddingLeft:32,width:"100%"}}/>
        </div>
      </div>

      {/* Table */}
      <Card>
        {loading ? (
          <div style={{padding:40,textAlign:"center",color:T.muted}}>Loading…</div>
        ) : filtered.length===0 ? (
          <div style={{padding:"48px 24px",textAlign:"center"}}>
            <FileText size={36} style={{color:T.dim,marginBottom:12}}/>
            <div style={{fontSize:14,color:T.text,fontWeight:600}}>
              No {status||""}  claims
            </div>
          </div>
        ) : (
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
              <thead>
                <tr style={{borderBottom:`1px solid ${T.border}`}}>
                  {["Employee","Claim Title","Items","Amount","Status","Submitted","Action"].map(h=>(
                    <th key={h} style={{padding:"11px 16px",textAlign:"left",fontSize:10,
                      color:T.dim,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.07em"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c,i)=>(
                  <tr key={c.id} style={{borderBottom:`1px solid ${T.border}20`,
                    background:i%2===0?"transparent":T.card2}}>
                    <td style={{padding:"12px 16px"}}>
                      <div style={{fontWeight:600,color:T.text}}>{c.employee_name}</div>
                      <div style={{fontSize:11,color:T.dim}}>{c.department}</div>
                    </td>
                    <td style={{padding:"12px 16px"}}>
                      <div style={{color:T.text,fontWeight:500}}>{c.title}</div>
                      {c.description&&<div style={{fontSize:11,color:T.dim,marginTop:2}}>{c.description}</div>}
                    </td>
                    <td style={{padding:"12px 16px",color:T.muted,fontFamily:"JetBrains Mono,monospace"}}>
                      {c.item_count}
                    </td>
                    <td style={{padding:"12px 16px"}}>
                      <div style={{fontFamily:"JetBrains Mono,monospace",fontWeight:700,color:T.cyan}}>
                        {fmtINR(c.total_amount)}
                      </div>
                      {c.approved_amount&&c.approved_amount!==c.total_amount&&(
                        <div style={{fontSize:11,color:T.green}}>
                          Approved: {fmtINR(c.approved_amount)}
                        </div>
                      )}
                    </td>
                    <td style={{padding:"12px 16px"}}>
                      <StatusBadge status={c.status}/>
                      {c.payroll_exported&&(
                        <div style={{fontSize:10,color:T.green,marginTop:3}}>
                          ✓ Payroll exported
                        </div>
                      )}
                    </td>
                    <td style={{padding:"12px 16px",color:T.muted,fontSize:12}}>
                      {c.submitted_at}
                    </td>
                    <td style={{padding:"12px 16px"}}>
                      <Btn size="sm" variant="ghost" onClick={()=>loadDetail(c)}>
                        <Eye size={11}/>Review
                      </Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {selected && (
        <ClaimModal
          claim={selected}
          onClose={()=>setSelected(null)}
          onAction={load}
          flash={flash}/>
      )}
    </div>
  );
}